// Login page — tabbed UI with two paths:
//   "SSO"          → SAML redirect (production default)
//   "Break-glass"  → email + password (local admin, always works)
//
// Centered card on starfield. SSO is the highlighted/default tab.
// Break-glass tab is admin-explicit ("emergency access") so daily-
// driver users don't accidentally land on it.

import { useState, useEffect, type FormEvent } from 'react';
import { Sparkles, ArrowRight, Lock, KeyRound, AlertTriangle, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Wordmark } from '../components/Logo';
import { ThemeToggle } from '../components/ui/ThemeToggle';
import { useMaintenance } from '../api/maintenance';
import { usePublicConfig } from '../api/publicConfig';
import { cn } from '../lib/cn';

type Tab = 'sso' | 'local';

export function Login() {
  const { data: maint } = useMaintenance();
  const { data: pubConfig } = usePublicConfig();
  // SAML off (saml.enabled=false) → only break-glass / OIDC. Undefined while
  // loading or on older builds is treated as enabled. When disabled we hide
  // the SSO tab and force the local panel.
  const samlEnabled = pubConfig?.saml_enabled !== false;
  const [tab, setTab] = useState<Tab>('sso');
  useEffect(() => {
    if (!samlEnabled) setTab('local');
  }, [samlEnabled]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onLocalSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch('/auth/local/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        if (res.status === 429) {
          setErr('Too many failed attempts. Account locked for 15 minutes.');
        } else if (res.status === 401) {
          setErr('Invalid email or password.');
        } else {
          const txt = await res.text();
          setErr(txt || `Login failed (${res.status}).`);
        }
        return;
      }
      globalThis.location.href = '/';
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="starfield-bg" aria-hidden="true" />
      <div className="min-h-screen flex flex-col">
        <header className="h-16 px-6 flex items-center justify-between">
          <Wordmark />
          <div className="flex items-center gap-3">
            {pubConfig?.help_url && (
              <a
                href={pubConfig.help_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-9 h-9 rounded-md text-fg-muted hover:text-fg hover:bg-bg-hover transition-colors"
                title="Help & documentation"
                aria-label="Help"
              >
                <HelpCircle className="h-4 w-4" />
              </a>
            )}
            <ThemeToggle />
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-6">
          <div className="w-full max-w-md relative">
            <div
              className="absolute inset-0 -z-10 blur-3xl opacity-40 pointer-events-none"
              style={{
                background:
                  tab === 'local'
                    ? 'radial-gradient(ellipse at center, rgb(var(--orb-warning) / 0.4), transparent 60%)'
                    : 'radial-gradient(ellipse at center, rgb(var(--orb-accent) / 0.5), transparent 60%)',
              }}
              aria-hidden="true"
            />

            <div className="orb-card overflow-hidden">
              {/* Tab strip — hidden when SAML is disabled (local-only sign-in) */}
              {samlEnabled && (
              <div className="flex border-b border-border bg-bg-inset/40">
                <button
                  type="button"
                  onClick={() => { setTab('sso'); setErr(null); }}
                  className={cn(
                    'flex-1 orb-tab justify-center',
                    tab === 'sso'
                      ? 'text-accent border-b-2 border-accent bg-bg-elevated'
                      : 'text-fg-muted hover:text-fg border-b-2 border-transparent',
                  )}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  SSO
                </button>
                <button
                  type="button"
                  onClick={() => { setTab('local'); setErr(null); }}
                  className={cn(
                    'flex-1 orb-tab justify-center',
                    tab === 'local'
                      ? 'text-warning border-b-2 border-warning bg-bg-elevated'
                      : 'text-fg-muted hover:text-fg border-b-2 border-transparent',
                  )}
                >
                  <KeyRound className="h-3.5 w-3.5" />
                  Break-glass
                </button>
              </div>
              )}

              <div className="p-8">
                {samlEnabled && tab === 'sso' ? (
                  <SSOPanel maintActive={!!maint?.active} />
                ) : (
                  <LocalPanel
                    email={email}
                    password={password}
                    busy={busy}
                    err={err}
                    setEmail={setEmail}
                    setPassword={setPassword}
                    onSubmit={onLocalSubmit}
                  />
                )}
              </div>
            </div>

            <p className="text-center text-xs text-fg-subtle mt-6">
              Trouble signing in?{' '}
              <a href="mailto:admin@example.com" className="text-accent hover:underline">
                contact your platform admin
              </a>
            </p>
          </div>
        </main>

        <footer className="h-12 px-6 flex items-center justify-center">
          <span className="orb-eyebrow text-fg-subtle">OrbitalReg · v1.0</span>
        </footer>
      </div>
    </>
  );
}

function SSOPanel({ maintActive }: Readonly<{ maintActive: boolean }>) {
  return (
    <>
      <div className="flex items-center justify-center mb-6">
        <div className="h-12 w-12 rounded-full bg-accent/10 border border-accent/30 flex items-center justify-center">
          <Sparkles className="h-5 w-5 text-accent" />
        </div>
      </div>

      <p className="orb-eyebrow text-center mb-2">Sign in</p>
      <h1 className="text-2xl font-display font-semibold text-center text-fg leading-tight">
        Welcome to <span className="tracking-wide">OrbitalReg</span>
      </h1>
      <p className="text-center text-fg-muted text-sm mt-2 mb-8">
        Sign in with your organisation's identity provider to continue.
      </p>

      {maintActive && (
        <div className="mb-5 p-3 rounded-md bg-warning/10 border border-warning/30 text-sm text-fg flex items-start gap-2">
          <Lock className="h-4 w-4 text-warning mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Maintenance mode is active</div>
            <div className="text-fg-muted">
              Reads still work; writes are blocked while the platform is in
              read-only mode.
            </div>
          </div>
        </div>
      )}

      <Button
        variant="primary"
        size="lg"
        fullWidth
        onClick={() => { globalThis.location.href = '/auth/login'; }}
      >
        Continue with SSO
        <ArrowRight className="h-4 w-4" />
      </Button>

      <div className="mt-6 pt-6 border-t border-border">
        <p className="text-xs text-fg-subtle text-center leading-relaxed">
          Authentication is handled by your organisation's SAML identity
          provider (Azure Entra ID, Okta, Auth0, …). Once authenticated,
          group claims map to roles (admin / developer / reader).
        </p>
      </div>
    </>
  );
}

interface LocalPanelProps {
  readonly email: string;
  readonly password: string;
  readonly busy: boolean;
  readonly err: string | null;
  readonly setEmail: (s: string) => void;
  readonly setPassword: (s: string) => void;
  readonly onSubmit: (e: FormEvent) => void;
}

function LocalPanel({ email, password, busy, err, setEmail, setPassword, onSubmit }: LocalPanelProps) {
  return (
    <>
      <div className="flex items-center justify-center mb-6">
        <div className="h-12 w-12 rounded-full bg-warning/10 border border-warning/30 flex items-center justify-center">
          <KeyRound className="h-5 w-5 text-warning" />
        </div>
      </div>

      <p className="orb-eyebrow text-center mb-2 text-warning/80">Emergency access</p>
      <h1 className="text-2xl font-display font-semibold text-center text-fg leading-tight">
        Break-glass sign-in
      </h1>
      <p className="text-center text-fg-muted text-sm mt-2 mb-6">
        Sign in with a locally-stored admin account. Use this when SSO is
        unreachable or for first-install bootstrapping.
      </p>

      <div className="mb-5 p-3 rounded-md bg-warning/10 border border-warning/30 text-xs text-fg-muted flex items-start gap-2">
        <AlertTriangle className="h-3.5 w-3.5 text-warning mt-0.5 shrink-0" />
        <div>
          <span className="text-fg font-medium">Audit logged.</span> Every
          break-glass login records the email + timestamp. After 5 failed
          attempts the account locks for 15 minutes.
        </div>
      </div>

      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label htmlFor="login-email" className="block text-sm font-medium text-fg mb-1.5">Email</label>
          <Input
            id="login-email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@example.com"
            autoFocus
          />
        </div>
        <div>
          <label htmlFor="login-password" className="block text-sm font-medium text-fg mb-1.5">Password</label>
          <Input
            id="login-password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••••••"
          />
        </div>

        {err && (
          <div className="p-3 rounded-md bg-danger/10 border border-danger/30 text-sm text-danger">
            {err}
          </div>
        )}

        <Button type="submit" variant="primary" size="lg" fullWidth disabled={busy}>
          <KeyRound className="h-4 w-4" />
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>

      <div className="mt-6 pt-6 border-t border-border">
        <p className="text-xs text-fg-subtle text-center leading-relaxed">
          Bootstrap admin credentials live in{' '}
          <code className="orb-code mx-1">ORBITALREG_BOOTSTRAP_ADMIN_*</code>{' '}
          environment variables on the server. Rotate the password after
          first sign-in via the admin user management page.
        </p>
      </div>
    </>
  );
}
